import { useQuery } from "@tanstack/react-query";
import { v2SearchClient } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";

/**
 * One clip of a game's condensed game. Field names match /v2/search wherever the
 * two overlap; index, offset, duration, start and the scores are the additions.
 *
 * `offset` is the clip's position in the condensed reel and is what the player
 * seeks to. `start` is its position in the source asset and is unused here.
 */
export type CondensedClip = {
  index: number;
  game_event_id: number;
  event_type: string;
  period: number | null;
  offset: number;
  duration: number;
  start: number | null;
  /**
   * The analytics side's team ids, which are TPE ids. The watch page holds the
   * tenant's own home_team_id_tpe and away_team_id_tpe, so these are what say
   * which crest belongs on which side rather than assuming the two agree.
   */
  home_team_id: number | null;
  away_team_id: number | null;
  home_score: number | null;
  away_score: number | null;
  game_date: string | null;
  home_team_name: string | null;
  away_team_name: string | null;
  player_first_name: string | null;
  player_last_name: string | null;
  player_position: string | null;
  player_jersey: number | null;
  team_name: string | null;
  thumbnail_url: string | null;
};

type ClipsResponse = { data?: { list?: CondensedClip[] } };

/**
 * The clips in this game's condensed game, in reel order.
 *
 * Deliberately sends no `date`: it is part of the playlist's cache key, and the
 * player's source url does not send one either. If the two disagree they can
 * resolve to playlists built at different moments, and every offset would then
 * describe the wrong reel.
 */
export function useCondensedGameClips(tpeGameId?: number | string | null) {
  const gameId = tpeGameId == null || tpeGameId === "" ? null : String(tpeGameId);

  return useQuery({
    queryKey: ["v2", "watch", "condensed-clips", gameId],
    enabled: Boolean(gameId),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const token = getCurrentAuth()?.AccessToken;
      const res = await v2SearchClient.get<ClipsResponse>(
        `${APIRoutes.CondensedGameClips}?game_ids=${encodeURIComponent(String(gameId))}`,
        {
          headers: token ? { Authorization: `bearer ${token}` } : {},
          signal,
        }
      );
      return res.data?.data?.list ?? [];
    },
  });
}
